import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Generic server-side pagination hook for any list. The vertical-agnostic sibling of
// useFinanceQuery - no FinanceContext, no routed-page assumption, so it is safe to use for
// several independent lists on one screen (e.g. the tabs of a dashboard).
//
//   const { rows, page, totalPages, loading, error, setPage, setLimit, reload } =
//     usePagedList((params) => adminListBookings({ ...filters, ...params }), {
//       deps: [filters.status, filters.q],   // changing a filter resets to page 1
//     });
//
// `loader(params)` is called with `{ page, limit }` and must return either the API envelope
// `{ data, pagination: { page, limit, total, hasMore } }` or a bare array (treated as a
// single complete page - lets a not-yet-paginated endpoint keep working).
//
// By default page/limit live in React state. Pass `urlKey: 'bookings'` to mirror them onto
// the URL as `?bookingsPage=&bookingsLimit=` instead (handy for a routed list page that
// should survive a refresh / be shareable). Different keys never collide, so two lists on
// one route can both URL-sync.

export const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_LIMIT = 20;

// Client-side pagination of an in-memory array - for lists the screen has already fetched
// and bucketed/sorted itself (e.g. a dashboard's "active / cancelled / completed" booking
// tabs, where the split and the priority sort happen in the browser and a raw server page
// would cut across buckets). Same return shape as usePagedList, so the same <Pager/> and
// render code work unchanged. `resetKey` (e.g. the active tab, a filter value) sends it back
// to page 1 when it changes.
export function usePagedArray(items, { initialLimit = DEFAULT_LIMIT, resetKey = '' } = {}) {
    const all = Array.isArray(items) ? items : [];
    const [page, setPageRaw] = useState(1);
    const [limit, setLimitRaw] = useState(initialLimit);

    useEffect(() => { setPageRaw(1); }, [resetKey]);
    // Clamp if the array shrank under our feet (a row was removed / a filter narrowed it).
    const totalPages = Math.max(1, Math.ceil(all.length / limit));
    const safePage = Math.min(page, totalPages);
    useEffect(() => { if (page > totalPages) setPageRaw(totalPages); }, [page, totalPages]);

    const setPage = useCallback((n) => setPageRaw(Math.max(1, n)), []);
    const setLimit = useCallback((n) => { setLimitRaw(n); setPageRaw(1); }, []);
    const start = (safePage - 1) * limit;

    return useMemo(() => ({
        rows: all.slice(start, start + limit),
        loading: false, error: '',
        page: safePage, limit, totalPages,
        hasMore: safePage < totalPages, total: all.length,
        setPage, setLimit, reload: () => {}
    }), [all, start, limit, safePage, totalPages, setPage, setLimit]);
}

export default function usePagedList(loader, {
    deps = [], initialLimit = DEFAULT_LIMIT, urlKey = '', enabled = true
} = {}) {
    const [params, setParams] = useSearchParams();
    const pageKey = urlKey ? `${urlKey}Page` : '';
    const limitKey = urlKey ? `${urlKey}Limit` : '';

    const [statePage, setStatePage] = useState(1);
    const [stateLimit, setStateLimit] = useState(initialLimit);

    const page = urlKey ? Math.max(1, Number(params.get(pageKey)) || 1) : statePage;
    const limit = urlKey ? (Number(params.get(limitKey)) || initialLimit) : stateLimit;

    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: initialLimit, total: 0, hasMore: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const reqId = useRef(0);

    const writeUrl = useCallback((patch) => {
        setParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(patch).forEach(([k, v]) => {
                if (v == null || v === '') next.delete(k);
                else next.set(k, String(v));
            });
            return next;
        }, { replace: true });
    }, [setParams]);

    const setPage = useCallback((n) => {
        const p = Math.max(1, n);
        if (urlKey) writeUrl({ [pageKey]: p === 1 ? '' : p });
        else setStatePage(p);
    }, [urlKey, pageKey, writeUrl]);

    const setLimit = useCallback((n) => {
        if (urlKey) writeUrl({ [limitKey]: n, [pageKey]: '' });
        else { setStateLimit(n); setStatePage(1); }
    }, [urlKey, limitKey, pageKey, writeUrl]);

    // A filter change (deps) resets to the first page.
    const depsSig = JSON.stringify(deps);
    const firstRun = useRef(true);
    useEffect(() => {
        if (firstRun.current) { firstRun.current = false; return; }
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depsSig]);

    const load = useCallback(async () => {
        if (!enabled) { setLoading(false); return; }
        const id = ++reqId.current;
        setLoading(true);
        setError('');
        try {
            const res = await loader({ page, limit });
            if (id !== reqId.current) return; // a newer request superseded this one
            if (Array.isArray(res)) {
                setRows(res);
                setPagination({ page: 1, limit: res.length || initialLimit, total: res.length, hasMore: false });
            } else {
                setRows(res?.data || []);
                setPagination(res?.pagination || { page, limit, total: (res?.data || []).length, hasMore: false });
            }
        } catch (err) {
            if (id !== reqId.current) return;
            setError(err?.response?.data?.msg || 'Could not load this list.');
            setRows([]);
        } finally {
            if (id === reqId.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit, enabled, depsSig]);

    useEffect(() => { load(); }, [load]);

    const totalPages = pagination.total && limit
        ? Math.max(1, Math.ceil(pagination.total / limit))
        : (pagination.hasMore ? page + 1 : page);

    return useMemo(() => ({
        rows, pagination, loading, error,
        page, limit, totalPages, hasMore: !!pagination.hasMore,
        total: pagination.total || 0,
        setPage, setLimit, reload: load
    }), [rows, pagination, loading, error, page, limit, totalPages, setPage, setLimit, load]);
}
